import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { VaultKeTheme } from '../utils/theme';
const Hero: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles: { [key: string]: React.CSSProperties } = {
    hero: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, rgba(13, 17, 23, 0.8) 0%, rgba(13, 17, 23, 0.6) 50%, rgba(13, 17, 23, 0.8) 100%), url('/landingpage.png')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: `${VaultKeTheme.spacing.xxl} ${VaultKeTheme.spacing.lg}`,
      position: 'relative',
      overflow: 'hidden',
    },
    container: {
      maxWidth: '1200px',
      width: '100%',
      textAlign: 'center',
      zIndex: 2,
    },
    title: {
      ...VaultKeTheme.typography.h1,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
      fontSize: 'clamp(2.5rem, 5vw, 4rem)',
      lineHeight: '1.2',
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      marginBottom: VaultKeTheme.spacing.xl,
      fontSize: 'clamp(1rem, 2vw, 1.25rem)',
      maxWidth: '600px',
      margin: `0 auto ${VaultKeTheme.spacing.xl}`,
    },
    buttonGroup: {
      display: 'flex',
      gap: VaultKeTheme.spacing.md,
      justifyContent: 'center',
      flexWrap: 'wrap',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'center',
    },
    primaryButton: {
      ...VaultKeTheme.components.button.primary,
      fontSize: isMobile ? '1rem' : '1.1rem',
      padding: isMobile ? '14px 24px' : '16px 32px',
      minWidth: isMobile ? '140px' : '160px',
      width: isMobile ? '100%' : 'auto',
      maxWidth: isMobile ? '200px' : 'none',
    },
    secondaryButton: {
      ...VaultKeTheme.components.button.secondary,
      fontSize: isMobile ? '1rem' : '1.1rem',
      padding: isMobile ? '14px 24px' : '16px 32px',
      minWidth: isMobile ? '140px' : '160px',
      width: isMobile ? '100%' : 'auto',
      maxWidth: isMobile ? '200px' : 'none',
    },
    backgroundPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.05,
      backgroundImage: `
        radial-gradient(circle at 20% 80%, ${VaultKeTheme.colors.primary} 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, ${VaultKeTheme.colors.primaryLight} 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, ${VaultKeTheme.colors.success} 0%, transparent 50%)
      `,
      zIndex: 1,
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
      },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: '0 10px 25px rgba(0, 212, 170, 0.3)',
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.95 },
  };

  return (
    <motion.section
      style={styles.hero}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div
        style={styles.backgroundPattern}
        animate={{
          backgroundPosition: ['0% 0%', '100% 100%'],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: 'reverse',
        }}
      ></motion.div>
      <div style={styles.container}>
        <motion.h1 style={styles.title} variants={itemVariants}>
          Automate Your Chama Today
        </motion.h1>
        <motion.p style={styles.subtitle} variants={itemVariants}>
          Transform your community savings group with cutting-edge technology.
          Experience secure transactions, AI-powered insights, and seamless management
          for unprecedented financial growth.
        </motion.p>
        <motion.div style={styles.buttonGroup} variants={itemVariants}>
          <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
            <Link to="/register" style={styles.primaryButton}>
              Get Started
            </Link>
          </motion.div>
          <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
            <Link to="/about" style={styles.secondaryButton}>
              Learn More
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Hero;